import React, { useState, useRef } from "react"
import { Modal } from "reactstrap"
import { Play, ChevronRight } from "lucide-react"

// ── Inline decorative background blobs (matches AgendaModal) ──────────────────
const BackgroundBlobs = () => (
  <div
    aria-hidden="true"
    style={{
      position: "absolute",
      inset: 0,
      overflow: "hidden",
      pointerEvents: "none",
      zIndex: 0,
    }}
  >
    {/* Top-right diagonal beam */}
    <div
      style={{
        position: "absolute",
        top: "-80px",
        right: "-40px",
        width: "260px",
        height: "520px",
        background:
          "linear-gradient(135deg, transparent 0%, rgba(43,127,255,0.07) 30%, rgba(90,173,255,0.2) 50%, rgba(43,127,255,0.07) 70%, transparent 100%)",
        transform: "rotate(-18deg)",
        animation: "beamDrift 8s ease-in-out infinite alternate",
      }}
    />
    {/* Secondary thinner beam */}
    <div
      style={{
        position: "absolute",
        top: "0px",
        right: "80px",
        width: "120px",
        height: "400px",
        background:
          "linear-gradient(135deg, transparent 0%, rgba(127,212,255,0.12) 50%, transparent 100%)",
        transform: "rotate(-18deg)",
        animation: "beamDrift 8s ease-in-out infinite alternate",
        animationDelay: "1s",
      }}
    />
    {/* Bottom-left glow blob */}
    <div
      style={{
        position: "absolute",
        bottom: "-60px",
        left: "-60px",
        width: "280px",
        height: "280px",
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(10,42,110,0.6) 0%, transparent 70%)",
      }}
    />
    {/* Spark dot — Walmart asterisk echo */}
    <div
      style={{
        position: "absolute",
        top: "120px",
        right: "72px",
        width: "30px",
        height: "30px",
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(255,194,32,0.85) 0%, transparent 70%)",
        boxShadow: "0 0 20px 6px rgba(255,194,32,0.35)",
        animation: "sparkPulse 3s ease-in-out infinite",
      }}
    />
  </div>
)

// ── Video List Item ────────────────────────────────────────────────────────────
const VideoListItem = ({ doc, idx, isActive, onClick }) => (
  <li
    style={{
      listStyle: "none",
      padding: "0",
      animation: "sessionFadeIn 0.4s ease both",
      animationDelay: `${idx * 0.055}s`,
    }}
  >
    <button
      onClick={onClick}
      style={{
        width: "100%",
        background: isActive
          ? "linear-gradient(135deg, rgba(43,127,255,0.22), rgba(10,42,110,0.55))"
          : "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))",
        border: `1px solid ${
          isActive ? "rgba(90,173,255,0.5)" : "rgba(90,173,255,0.12)"
        }`,
        borderRadius: "8px",
        padding: "12px 14px",
        marginBottom: "8px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        textAlign: "left",
        transition: "all 0.25s ease",
        backdropFilter: "blur(6px)",
        boxShadow: isActive
          ? "0 0 18px rgba(43,127,255,0.2), inset 0 1px 0 rgba(255,255,255,0.08)"
          : "none",
      }}
      onMouseEnter={e => {
        if (!isActive) {
          e.currentTarget.style.background =
            "linear-gradient(135deg, rgba(43,127,255,0.12), rgba(10,42,110,0.35))"
          e.currentTarget.style.borderColor = "rgba(90,173,255,0.3)"
        }
      }}
      onMouseLeave={e => {
        if (!isActive) {
          e.currentTarget.style.background =
            "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))"
          e.currentTarget.style.borderColor = "rgba(90,173,255,0.12)"
        }
      }}
    >
      {/* Play indicator dot */}
      <div
        style={{
          width: "28px",
          height: "28px",
          borderRadius: "50%",
          background: isActive
            ? "linear-gradient(135deg, #2b7fff, #1a5fd4)"
            : "rgba(43,127,255,0.15)",
          border: `1px solid ${
            isActive ? "rgba(90,173,255,0.8)" : "rgba(90,173,255,0.25)"
          }`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          boxShadow: isActive ? "0 0 10px rgba(43,127,255,0.5)" : "none",
          transition: "all 0.25s ease",
        }}
      >
        <Play
          size={10}
          fill={isActive ? "#fff" : "rgba(90,173,255,0.7)"}
          color={isActive ? "#fff" : "rgba(90,173,255,0.7)"}
          style={{ marginLeft: "2px" }}
        />
      </div>

      {/* Title */}
      <span
        style={{
          // fontFamily: "'Rajdhani', sans-serif",
          fontSize: "13px",
          fontWeight: isActive ? 600 : 500,
          color: isActive ? "#e8f4ff" : "rgba(127,212,255,0.7)",
          letterSpacing: "0.3px",
          lineHeight: 1.3,
          flex: 1,
          transition: "color 0.2s ease",
        }}
      >
        {doc.name}
      </span>

      {/* Active chevron */}
      {isActive && (
        <ChevronRight
          size={14}
          color="rgba(90,173,255,0.6)"
          style={{ flexShrink: 0 }}
        />
      )}
    </button>
  </li>
)

// ── Main Component ─────────────────────────────────────────────────────────────
const VideoLibraryModal = ({ rc, onOpenRC, Alldata, curRC }) => {
  const areaData = Alldata?.data?.videolibrary?.data?.areas?.[curRC]
  const [activeVideoUrl, setActiveVideoUrl] = useState(
    areaData?.data?.[0]?.url || ""
  )
  const [activeVideoName, setActiveVideoName] = useState(
    areaData?.data?.[0]?.name || ""
  )
  const iframeRef = useRef(null)

  const handleVideoSelect = doc => {
    setActiveVideoUrl(doc.url)
    setActiveVideoName(doc.name)
    if (iframeRef.current) {
      iframeRef.current.src = doc.url
    }
  }

  return (
    <>
      {/* ── Keyframe injection ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&family=Exo+2:wght@300;400;500;600;700&display=swap');

        @keyframes beamDrift {
          from { opacity: 0.6; transform: rotate(-18deg) translateX(0); }
          to   { opacity: 1;   transform: rotate(-18deg) translateX(20px); }
        }
        @keyframes sparkPulse {
          0%,100% { opacity:0.8; transform:scale(1); }
          50%      { opacity:0.4; transform:scale(0.7); }
        }
        @keyframes sessionFadeIn {
          from { opacity:0; transform:translateY(14px); }
          to   { opacity:1; transform:translateY(0); }
        }

        .vl-modal .modal-dialog {
          max-width: 820px;
          margin: 20px auto;
        }
        .vl-modal .modal-content {
          background: transparent;
          border: none;
          border-radius: 16px;
          overflow: hidden;
          // font-family: 'Exo 2', sans-serif;
        }
        .vl-iframe-wrap {
          position: relative;
          width: 100%;
          padding-top: 56.25%;
          border-radius: 10px;
          overflow: hidden;
          background: #000;
          border: 1px solid rgba(90,173,255,0.2);
          box-shadow: 0 0 40px rgba(10,42,110,0.6), 0 0 80px rgba(43,127,255,0.1);
        }
        .vl-iframe-wrap iframe {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: none;
        }
        .vl-scroll::-webkit-scrollbar { width: 3px; }
        .vl-scroll::-webkit-scrollbar-track { background: transparent; }
        .vl-scroll::-webkit-scrollbar-thumb {
          background: #1a5fd4;
          border-radius: 2px;
        }
      `}</style>

      <Modal
        size="xl"
        isOpen={rc}
        toggle={onOpenRC}
        centered
        className="vl-modal"
        style={{ overflowY: "auto" }}
      >
        <div
          style={{
            position: "relative",
            background:
              "linear-gradient(175deg, rgb(6, 19, 48) 0%, rgba(2, 11, 26, 0.97) 45%, rgba(0, 0, 0, 0.98) 100%)",
            overflow: "hidden",
            borderRadius: "16px",
            border: "1px solid rgba(90,173,255,0.18)",
            boxShadow:
              "0 24px 80px rgba(0,0,0,0.8), 0 0 0 1px rgba(43,127,255,0.1)",
          }}
        >
          <BackgroundBlobs />

          {/* ── Header ── */}
          <div
            style={{
              position: "relative",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "18px 22px 14px",
              borderBottom: "1px solid rgba(90,173,255,0.15)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {/* Section label */}
              <span
                style={{
                  fontFamily: "'Rajdhani', sans-serif",
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "2.5px",
                  textTransform: "uppercase",
                  color: "rgba(255,194,32,0.85)",
                  marginRight: "2px",
                }}
              >
                {/* Video Library */}
              </span>
              <span style={{ color: "rgba(90,173,255,0.3)", fontSize: "14px" }}>
                
              </span>
              {/* Area title */}
              <span
                style={{
                  fontFamily: "'Rajdhani', sans-serif",
                  fontSize: "20px",
                  fontWeight: 700,
                  letterSpacing: "2px",
                  textTransform: "uppercase",
                  background: "linear-gradient(90deg, #e8f4ff, #7fd4ff)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {areaData?.name || "Videos"}
              </span>
            </div>

            {/* Close button */}
            <button
              onClick={onOpenRC}
              style={{
                background: "rgba(43,127,255,0.1)",
                border: "1px solid rgba(90,173,255,0.25)",
                borderRadius: "50%",
                color: "#5aadff",
                fontSize: "18px",
                lineHeight: 1,
                cursor: "pointer",
                textShadow: "0 0 8px rgba(43,127,255,0.6)",
                padding: "0",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
                flexShrink: 0,
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = "rgba(43,127,255,0.22)"
                e.currentTarget.style.borderColor = "rgba(90,173,255,0.5)"
                e.currentTarget.style.boxShadow =
                  "0 0 14px rgba(43,127,255,0.4)"
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = "rgba(43,127,255,0.1)"
                e.currentTarget.style.borderColor = "rgba(90,173,255,0.25)"
                e.currentTarget.style.boxShadow = "none"
              }}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* ── Body ── */}
          <div
            style={{
              position: "relative",
              zIndex: 5,
              padding: "20px 22px 24px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "15px"
              }}
            >
              {/* ── Left: Video Player ── */}
              <div style={{ minWidth: 0 }}>
                {/* Now playing label */}
                {activeVideoName && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "7px",
                      marginBottom: "10px",
                    }}
                  >
                    {/* Pulse dot */}
                    <div
                      style={{
                        width: "7px",
                        height: "7px",
                        borderRadius: "50%",
                        background: "#ffc220",
                        boxShadow: "0 0 8px rgba(255,194,32,0.7)",
                        animation: "sparkPulse 1.8s ease-in-out infinite",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontFamily: "'Exo 2', sans-serif",
                        fontSize: "11px",
                        fontWeight: 600,
                        letterSpacing: "1.5px",
                        textTransform: "uppercase",
                        color: "rgba(127,212,255,0.6)",
                      }}
                    >
                      Now Playing
                    </span>
                    <span
                      style={{
                        // fontFamily: "'Rajdhani', sans-serif",
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#e8f4ff",
                        letterSpacing: "0.3px",
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {activeVideoName}
                    </span>
                  </div>
                )}

                {/* iframe wrapper */}
                <div className="vl-iframe-wrap">
                  <iframe
                    ref={iframeRef}
                    id={areaData?.name}
                    src={activeVideoUrl}
                    title={activeVideoName}
                    allowFullScreen
                  />
                </div>

                {/* Glow line under player */}
                <div
                  style={{
                    height: "1px",
                    background:
                      "linear-gradient(90deg, transparent, rgba(43,127,255,0.4), rgba(255,194,32,0.3), transparent)",
                    marginTop: "12px",
                    borderRadius: "1px",
                  }}
                />
              </div>

              {/* ── Right: Video List ── */}
              <div
                style={{
                  flex: "0 0 240px",
                  minWidth: "200px",
                }}
              >
                {/* List header */}
                <div
                  style={{
                    // fontFamily: "'Rajdhani', sans-serif",
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    color: "rgba(90,173,255,0.5)",
                    marginBottom: "12px",
                    paddingLeft: "2px",
                  }}
                >
                  {areaData?.data?.length || 0} Videos
                </div>

                {/* Scrollable list */}
                <ul
                  className="vl-scroll"
                  style={{
                    margin: 0,
                    padding: 0,
                    overflowY: "auto",
                    maxHeight: "calc(56.25vw * 0.5 + 60px)",
                    maxHeight: "360px",
                    paddingRight: "4px",
                  }}
                >
                  {areaData?.data?.map((doc, ind) => (
                    <VideoListItem
                      key={ind}
                      doc={doc}
                      idx={ind}
                      isActive={activeVideoUrl === doc.url}
                      onClick={() => handleVideoSelect(doc)}
                    />
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom accent line */}
          <div
            style={{
              height: "2px",
              background:
                "linear-gradient(90deg, rgba(43,127,255,0.6), rgba(255,194,32,0.4), rgba(43,127,255,0.6))",
              position: "relative",
              zIndex: 10,
            }}
          />
        </div>
      </Modal>
    </>
  )
}

export default VideoLibraryModal